import './ExecutiveDashboard.css';

import React, { useEffect, useState } from 'react';

import GuideBanner from './GuideBanner';
import GuideLink from './GuideLink';
import axios from 'axios';
import { useDataEnvironment } from '../dataEnvironment';
import { useNavigate } from 'react-router-dom';

interface CoveragePoint {
  snapshotDate: string;
  visible: number;
  total: number;
  visiblePercentage: number;
  /** Which SQL definition produced the denominator; a change is a rebase, not movement. */
  rosterDefinition: string | null;
}

interface MetricPoint {
  snapshotDate: string;
  bookable: number;
  p1: number;
  p2: number;
  p3: number;
  rosterDefinition: string | null;
}

interface DeepCategory {
  code: string;
  label: string;
  description: string;
  owner: string | null;
  providers: number;
  actionable: boolean;
}

interface DeepFindings {
  probed: number;
  epicBuildProblem: number;
  capacity: number;
  categories: DeepCategory[];
  bookableElsewhere: number;
  snapshotDate: string | null;
}

interface DepartmentOpportunity {
  departmentId: string;
  departmentName: string | null;
  providersBehindFix: number;
  deptFlagProviderCount: number;
  deepBuildProviderCount: number;
  topVisitType: string | null;
  noteStatus: string | null;
}

interface DepartmentLeverage {
  actionableDepartments: number;
  deptFlagFixes: number;
  deepBuildFixes: number;
  top: DepartmentOpportunity[];
  snapshotDate: string | null;
}

interface ExecutiveSummary {
  coverage: {
    visible: number;
    total: number;
    percentage: number;
    changeSincePrevious: number | null;
    previousSnapshotDate: string | null;
    publicVisible: number;
    publicPercentage: number;
    publishingGap: { notOnLocation: number; noLocation: number; unverified: number };
  };
  impact: {
    invisibleToPatients: number;
    partiallyVisible: number;
    notYetPatientFacing: number;
    maintenanceOnly: number;
    inactiveExcluded: number;
    notCoveredByImport: number;
    rosterReconciled: boolean;
  };
  resolution: {
    notVisible: number;
    breakdown: { p1: number; p3: number; capacityOnly: number; maintenance: number };
    stages: { label: string; count: number }[];
    noNoteYet: number;
    totalOpen: number;
    excludedByWorkflow: number;
    /** Deducted from totalOpen; tracked once in the department queue instead. */
    coveredByDepartmentItem: number;
  };
  momentum: {
    gained: number;
    lost: number;
    net: number;
    comparedWith: string | null;
    coverageWarning: string | null;
    agedOverThirtyDays: number | null;
  };
  /** Null until an import carrying the Level 2 deep probe has been ingested. */
  deepFindings: DeepFindings | null;
  /** Null until an import carrying department rollups has been ingested. */
  departmentLeverage: DepartmentLeverage | null;
  freshness: {
    weeklySnapshotDate: string | null;
    weeklyIngestedAt: string | null;
    nightlySnapshotDate: string | null;
    nightlyIngestedAt: string | null;
    refreshedCount: number;
  };
  coverageTrend: {
    weekly: CoveragePoint[];
    nightly: CoveragePoint[];
  };
  metricTrend: {
    weekly: MetricPoint[];
    nightly: MetricPoint[];
  };
  generatedAt: string;
}

const number = (value: number): string => value.toLocaleString();

const shortDate = (iso: string | null): string => {
  if (!iso) return 'not available';
  // A date-only snapshot date parses as UTC midnight, which renders as the day
  // before anywhere west of Greenwich; read it as a local calendar date instead.
  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  const ms = dateOnly
    ? new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3])).getTime()
    : Date.parse(iso);
  if (!Number.isFinite(ms)) return iso;
  return new Date(ms).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

const dateAndTime = (iso: string | null): string => {
  if (!iso) return 'not available';
  const ms = Date.parse(iso);
  if (!Number.isFinite(ms)) return iso;
  return new Date(ms).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  });
};

const BAR_MAX_HEIGHT = 54;
const BAR_MIN_HEIGHT = 4;

/**
 * The plotted window, padded by a quarter of the observed span rather than a
 * fixed amount. Coverage has moved within a third of a point across recent
 * imports, and any fixed pad wide enough for a volatile series flattens that
 * one into identical bars; the floor stops a truly unchanged series being
 * magnified into noise.
 */
const barScale = (values: number[]): { low: number; high: number; span: number } => {
  const min = Math.min(...values);
  const max = Math.max(...values);
  const pad = Math.max((max - min) * 0.25, 0.2);
  const low = Math.max(0, Math.round((min - pad) * 10) / 10);
  const high = Math.min(100, Math.round((max + pad) * 10) / 10);
  return { low, high, span: high - low || 1 };
};

const movement = (points: CoveragePoint[]): number | null =>
  points.length < 2
    ? null
    : Math.round((points[points.length - 1].visiblePercentage - points[0].visiblePercentage) * 10) / 10;

/** The contiguous run measured with the newest point's roster definition. */
const currentDefinitionRun = (points: CoveragePoint[]): CoveragePoint[] => {
  if (points.length === 0) return [];
  const definition = points[points.length - 1].rosterDefinition;
  let start = points.length - 1;
  while (start > 0 && points[start - 1].rosterDefinition === definition) start -= 1;
  return points.slice(start);
};

const seriesRange = (points: CoveragePoint[]): string => {
  const comparable = currentDefinitionRun(points);
  if (comparable.length === 0) return 'unavailable';
  const last = comparable[comparable.length - 1];
  return comparable.length === 1
    ? `${pct(last.visiblePercentage)} under the current roster definition`
    : `${pct(comparable[0].visiblePercentage)} to ${pct(last.visiblePercentage)}`;
};

/** One decimal throughout, so a whole number does not render beside "52.5%" as "60%". */
const pct = (value: number): string => `${value.toFixed(1)}%`;

const SeriesSummary: React.FC<{ label: string; points: CoveragePoint[]; swatch: string }> = ({
  label,
  points,
  swatch
}) => {
  if (points.length === 0) return null;
  const comparable = currentDefinitionRun(points);
  const move = movement(comparable);
  const last = comparable[comparable.length - 1];
  const definitionChanged = comparable.length < points.length;
  return (
    <div className="exec-trend-legend-row">
      <span className={`exec-trend-swatch ${swatch}`} />
      <span className="exec-trend-legend-label">{label}</span>
      <span className="exec-trend-legend-value">
        {comparable.length === 1
          ? `${pct(last.visiblePercentage)} (${definitionChanged ? 'new roster definition' : 'one import'})`
          : `${pct(comparable[0].visiblePercentage)} → ${pct(last.visiblePercentage)}`}
      </span>
      {move !== null && (
        <span className={`exec-trend-legend-move exec-trend-${move > 0 ? 'up' : move < 0 ? 'down' : 'flat'}`}>
          {move === 0 ? 'no net change' : `${move > 0 ? '+' : ''}${move} pts`}
        </span>
      )}
    </div>
  );
};

/**
 * Coverage from both feeds on one axis, drawn as bars to match the digest email.
 *
 * The two series are kept visually distinct rather than joined into one line:
 * on the single date both cover, the weekly workbook reads 52.5% and the
 * nightly scan 59.6% over the same roster. A single line across that boundary
 * would draw a 7-point step that looks like remediation and is not.
 *
 * Both series share one scale so the offset between them is legible; that scale
 * is truncated to the observed range and says so, because coverage otherwise
 * moves in fractions of a point and a 0-100 axis reports nothing at all.
 */
const CoverageTrendChart: React.FC<{ weekly: CoveragePoint[]; nightly: CoveragePoint[] }> = ({
  weekly,
  nightly
}) => {
  const bars = [
    ...weekly.map((point) => ({ ...point, series: 'weekly' as const })),
    ...nightly.map((point) => ({ ...point, series: 'nightly' as const }))
  ].sort((left, right) =>
    left.snapshotDate === right.snapshotDate
      ? left.series === 'weekly'
        ? -1
        : 1
      : left.snapshotDate < right.snapshotDate
        ? -1
        : 1
  );

  if (bars.length < 2) {
    return (
      <div className="exec-trend">
        <p className="exec-trend-empty">
          Only one configuration import is available, so there is no trend to plot yet.
        </p>
      </div>
    );
  }

  const { low, high, span } = barScale(bars.map((bar) => bar.visiblePercentage));

  return (
    <div className="exec-trend">
      <div
        className="exec-trend-bars"
        role="img"
        aria-label={`Patient-visible coverage by import. Weekly workbook ${
          seriesRange(weekly)
        }; nightly scan ${seriesRange(nightly)}.`}
      >
        {bars.map((bar, index) => {
          const isCurrent = index === bars.length - 1;
          // A denominator change is not movement, so the series is broken rather
          // than drawn continuously across it.
          const rebased =
            index > 0 && bar.rosterDefinition !== bars[index - 1].rosterDefinition;
          const height =
            BAR_MIN_HEIGHT + Math.round(((bar.visiblePercentage - low) / span) * BAR_MAX_HEIGHT);
          return (
            <React.Fragment key={`${bar.series}-${bar.snapshotDate}`}>
              {rebased && (
                <div
                  className="exec-trend-break"
                  title="The roster definition changed here, so points either side are measured against different populations."
                  aria-label="Roster definition changed"
                />
              )}
              <div className="exec-trend-col">
                <span className={`exec-trend-value${isCurrent ? ' exec-trend-value-current' : ''}`}>
                  {pct(bar.visiblePercentage)}
                </span>
                <div
                  className={`exec-trend-bar exec-trend-bar-${bar.series}`}
                  style={{ height: `${height}px` }}
                  title={`${bar.series === 'weekly' ? 'Weekly workbook' : 'Nightly scan'} ${bar.snapshotDate}: ${pct(bar.visiblePercentage)} (${number(bar.visible)} of ${number(bar.total)})`}
                />
                <span className="exec-trend-date">{shortDate(bar.snapshotDate)}</span>
              </div>
            </React.Fragment>
          );
        })}
      </div>

      <div className="exec-trend-legend">
        <SeriesSummary label="Weekly workbook" points={weekly} swatch="exec-trend-swatch-weekly" />
        <SeriesSummary label="Nightly scan" points={nightly} swatch="exec-trend-swatch-nightly" />
      </div>

      <div className="exec-trend-axis">
        Bars are scaled between {low}% and {high}%, not zero-based. The two feeds are reported
        separately because they disagree: where their dates overlap the nightly scan reads higher
        than the workbook over the same roster, so the step between them is a change of source, not
        progress. The headline reflects the nightly feed. Imports taken before the roster was
        captured with the snapshot are not plotted, because the population they were measured
        against can no longer be established. A vertical rule marks a change in the roster
        definition, where points either side count different populations.
      </div>
    </div>
  );
};

const METRIC_SERIES: { key: 'bookable' | 'p1' | 'p2' | 'p3'; label: string; color: string }[] = [
  { key: 'bookable', label: 'Bookable in Epic', color: '#0f3f74' },
  { key: 'p1', label: 'P1', color: '#a4262c' },
  { key: 'p2', label: 'P2', color: '#c98a12' },
  { key: 'p3', label: 'P3', color: '#5a6478' }
];

const CHART_W = 760;
const CHART_H = 240;
const PAD = { left: 48, right: 16, top: 16, bottom: 28 };

const niceMax = (value: number): number => {
  if (value <= 0) return 10;
  const step = Math.pow(10, Math.floor(Math.log10(value)));
  return Math.ceil(value / step) * step;
};

/**
 * Bookable numerator and the P1/P2/P3 counts over time, one line each on a
 * shared zero-based axis. One feed at a time: the weekly workbook and nightly
 * scan disagree on the same date, so joining them would draw a step that is a
 * change of source. A line is broken where the roster definition changes.
 */
const MetricTrendChart: React.FC<{ weekly: MetricPoint[]; nightly: MetricPoint[] }> = ({
  weekly,
  nightly
}) => {
  const [feed, setFeed] = useState<'weekly' | 'nightly'>(nightly.length >= 2 ? 'nightly' : 'weekly');
  const [hovered, setHovered] = useState<number | null>(null);
  const points = feed === 'nightly' ? nightly : weekly;

  const selector = (
    <div className="exec-legend" role="group" aria-label="Trend source">
      {(['nightly', 'weekly'] as const).map((option) => (
        <button
          key={option}
          type="button"
          className="exec-link"
          aria-pressed={feed === option}
          style={{ fontWeight: feed === option ? 700 : 400 }}
          onClick={() => {
            setFeed(option);
            setHovered(null);
          }}
        >
          {option === 'nightly' ? 'Nightly scan' : 'Weekly workbook'}
        </button>
      ))}
    </div>
  );

  if (points.length < 2) {
    return (
      <div className="exec-trend">
        {selector}
        <p className="exec-chart-empty">Fewer than two imports are available for this source, so there is no trend to plot yet.</p>
      </div>
    );
  }

  const max = niceMax(Math.max(...points.flatMap((p) => METRIC_SERIES.map((s) => p[s.key]))));
  const plotW = CHART_W - PAD.left - PAD.right;
  const plotH = CHART_H - PAD.top - PAD.bottom;
  const x = (index: number): number => PAD.left + (plotW * index) / (points.length - 1);
  const y = (value: number): number => PAD.top + plotH - (value / max) * plotH;
  const ticks = [0, 0.25, 0.5, 0.75, 1].map((fraction) => Math.round(max * fraction));

  const pathFor = (key: typeof METRIC_SERIES[number]['key']): string => {
    let d = '';
    points.forEach((point, index) => {
      const rebased = index > 0 && point.rosterDefinition !== points[index - 1].rosterDefinition;
      d += `${index === 0 || rebased ? 'M' : 'L'}${x(index).toFixed(1)},${y(point[key]).toFixed(1)} `;
    });
    return d.trim();
  };

  const last = points[points.length - 1];
  const hoveredPoint = hovered !== null ? points[hovered] : null;
  const step = plotW / (points.length - 1);
  const TIP_W = 150;
  const TIP_H = 22 + METRIC_SERIES.length * 15;

  return (
    <div className="exec-trend">
      {selector}
      <svg
        className="exec-chart"
        viewBox={`0 0 ${CHART_W} ${CHART_H}`}
        role="img"
        onMouseLeave={() => setHovered(null)}
        aria-label={`Bookable providers and P1, P2, P3 counts by import. Latest ${shortDate(last.snapshotDate)}: ${number(last.bookable)} bookable, P1 ${number(last.p1)}, P2 ${number(last.p2)}, P3 ${number(last.p3)}.`}
      >
        {ticks.map((tick) => (
          <g key={tick}>
            <line x1={PAD.left} x2={CHART_W - PAD.right} y1={y(tick)} y2={y(tick)} stroke="#e3e8ef" />
            <text x={PAD.left - 6} y={y(tick) + 3} textAnchor="end" fontSize="10" fill="#8a94a6">
              {number(tick)}
            </text>
          </g>
        ))}
        {points.map((point, index) => (
          <text key={point.snapshotDate} x={x(index)} y={CHART_H - 8} textAnchor="middle" fontSize="10" fill="#8a94a6">
            {shortDate(point.snapshotDate)}
          </text>
        ))}
        {hoveredPoint && (
          <line
            x1={x(hovered!)}
            x2={x(hovered!)}
            y1={PAD.top}
            y2={PAD.top + plotH}
            stroke="#94a3b8"
            strokeDasharray="3 3"
          />
        )}
        {METRIC_SERIES.map((series) => (
          <g key={series.key}>
            <path d={pathFor(series.key)} fill="none" stroke={series.color} strokeWidth="2" />
            {points.map((point, index) => (
              <circle
                key={point.snapshotDate}
                cx={x(index)}
                cy={y(point[series.key])}
                r={hovered === index ? 5 : 3}
                fill={series.color}
              />
            ))}
            {hovered === null && (
              <text x={x(points.length - 1)} y={y(last[series.key]) - 7} textAnchor="end" fontSize="10" fontWeight="700" fill={series.color}>
                {number(last[series.key])}
              </text>
            )}
          </g>
        ))}
        {points.map((point, index) => (
          <rect
            key={point.snapshotDate}
            x={x(index) - step / 2}
            y={PAD.top}
            width={step}
            height={plotH}
            fill="transparent"
            onMouseEnter={() => setHovered(index)}
          />
        ))}
        {hoveredPoint && (() => {
          const px = x(hovered!);
          const tipX = px + 10 + TIP_W > CHART_W - PAD.right ? px - 10 - TIP_W : px + 10;
          return (
            <g pointerEvents="none">
              <rect x={tipX} y={PAD.top} width={TIP_W} height={TIP_H} rx="4" fill="#ffffff" stroke="#cbd5e1" />
              <text x={tipX + 10} y={PAD.top + 16} fontSize="11" fontWeight="700" fill="#1f2937">
                {shortDate(hoveredPoint.snapshotDate)}
              </text>
              {METRIC_SERIES.map((series, row) => (
                <g key={series.key}>
                  <circle cx={tipX + 14} cy={PAD.top + 28 + row * 15} r="4" fill={series.color} />
                  <text x={tipX + 24} y={PAD.top + 32 + row * 15} fontSize="11" fill="#475569">
                    {series.label}
                  </text>
                  <text x={tipX + TIP_W - 10} y={PAD.top + 32 + row * 15} fontSize="11" fontWeight="700" textAnchor="end" fill="#1f2937">
                    {number(hoveredPoint[series.key])}
                  </text>
                </g>
              ))}
            </g>
          );
        })()}
      </svg>
      <div className="exec-legend">
        {METRIC_SERIES.map((series) => (
          <span key={series.key} className="exec-legend-item">
            <i style={{ background: series.color }} className="exec-legend-swatch" />
            {series.label}
          </span>
        ))}
      </div>
      <div className="exec-trend-axis">
        Zero-based axis, provider counts. P1/P2/P3 are counted as in the impact cards. A line breaks
        where the roster definition changed, because points either side count different populations.
      </div>
    </div>
  );
};

const ChangePill: React.FC<{ value: number | null; suffix: string; since: string | null }> = ({
  value,
  suffix,
  since
}) => {
  if (value === null || !since) {
    return <span className="exec-change exec-change-flat">No earlier baseline to compare</span>;
  }
  const direction = value > 0 ? 'up' : value < 0 ? 'down' : 'flat';
  const sign = value > 0 ? '+' : '';
  return (
    <span className={`exec-change exec-change-${direction}`}>
      {sign}
      {value}
      {suffix} since {shortDate(since)}
    </span>
  );
};

const ExecutiveDashboard: React.FC = () => {
  const [summary, setSummary] = useState<ExecutiveSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const environment = useDataEnvironment();

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await axios.get('/api/epic-blocks/executive-summary');
        if (!cancelled) setSummary(response.data);
      } catch (err: any) {
        if (!cancelled) {
          setError(err?.response?.data?.error || 'Could not load the summary.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [environment]);

  if (loading) {
    return <div className="exec-dashboard"><p className="exec-loading">Loading…</p></div>;
  }

  if (error || !summary) {
    return (
      <div className="exec-dashboard">
        <div className="exec-error">{error || 'No summary is available.'}</div>
      </div>
    );
  }

  const { coverage, impact, resolution, momentum, deepFindings, departmentLeverage, freshness, coverageTrend, metricTrend } = summary;
  const liveImpact = impact.invisibleToPatients + impact.partiallyVisible;
  const funnelPeak = Math.max(...resolution.stages.map((stage) => stage.count), 1);
  const publishingGapTotal = coverage.publishingGap
    ? coverage.publishingGap.notOnLocation + coverage.publishingGap.noLocation
    : 0;
  const netDirection = momentum.net > 0 ? 'positive' : momentum.net < 0 ? 'negative' : 'flat';

  const openAudit = (priority?: string) =>
    navigate(priority ? `/provider-queue?priority=${priority}` : '/provider-queue');

  return (
    <div className="exec-dashboard">
      <GuideBanner storageKey="dashboard" section="start-here">
        <strong>New here?</strong> Read the top three sections, then take your work from the
        department queue before the provider list &mdash; one Epic change there can unblock dozens of
        providers at once.
      </GuideBanner>

      <header className="exec-header">
        <h1>
          Online Scheduling Visibility
          <GuideLink section="dashboard" label="How to read this page" spaced />
        </h1>
        <p className="exec-headline">
          <strong>
            {number(coverage.visible)} of {number(coverage.total)} providers ({coverage.percentage}%)
          </strong>{' '}
          on the Open Scheduling roster currently have appointments patients can find and book online.
        </p>
        <p className="exec-freshness">
          Full Epic configuration refreshed weekly (last: {shortDate(freshness.weeklySnapshotDate)}).{' '}
          {freshness.nightlySnapshotDate ? (
            <>
              Scheduling rules refreshed nightly (last: {dateAndTime(freshness.nightlyIngestedAt)};{' '}
              {number(freshness.refreshedCount)} providers refreshed).
            </>
          ) : (
            <span className="exec-pending">
              The nightly rules refresh is not yet running in this environment, so every figure below
              reflects the weekly workbook alone.
            </span>
          )}
        </p>
      </header>

      <section className="exec-section">
        <h2>
          Patient-visible coverage
          <GuideLink section="dashboard-coverage" spaced />
        </h2>
        <p className="exec-section-note">
          Measured against the Open Scheduling roster — providers marked active and available for
          online scheduling in the provider directory. The directory defines who should be bookable;
          the Epic configuration import only describes how they are set up.
        </p>
        <div className="exec-coverage">
          <div>
            <div className="exec-big-number">{coverage.percentage}%</div>
            <div className="exec-big-caption">
              {number(coverage.visible)} of {number(coverage.total)} providers on the roster are
              bookable in Epic
            </div>
          </div>
          <ChangePill
            value={coverage.changeSincePrevious}
            suffix=" pts"
            since={coverage.previousSnapshotDate}
          />
          <div className="exec-public-coverage">
            <div className="exec-public-number">{coverage.publicPercentage}%</div>
            <div className="exec-big-caption">
              {number(coverage.publicVisible)} bookable on the public site
            </div>
          </div>
        </div>
        <CoverageTrendChart weekly={coverageTrend.weekly} nightly={coverageTrend.nightly} />
        {publishingGapTotal > 0 && (
          <div className="exec-callout">
            <strong>{number(publishingGapTotal)}</strong> providers are bookable in Epic but not on
            the public site. Their slots are at departments that are not on their published Marketing
            Master locations ({number(coverage.publishingGap.notOnLocation)}), or they have no
            published location at all ({number(coverage.publishingGap.noLocation)}). This is Symplr
            location work, not Epic work, so it is not in the panels below.{' '}
            <button
              type="button"
              className="exec-link"
              onClick={() => navigate('/provider-queue?view=publishing')}
            >
              Review in the Publishing view
            </button>
            .
            {coverage.publishingGap.unverified > 0 && (
              <>
                {' '}A further {number(coverage.publishingGap.unverified)} could not be checked: Epic
                reports slots, but the scan recorded no slot department.
              </>
            )}
          </div>
        )}
        {impact.notCoveredByImport > 0 && (
          <div className="exec-callout exec-callout-warning">
            <strong>{number(impact.notCoveredByImport)}</strong> providers on the roster were not
            described by the latest configuration import at all. Nothing is known about their Epic
            setup, so they count as not visible until an import covers them.
          </div>
        )}
        {!impact.rosterReconciled && (
          <p className="exec-footnote">
            The latest import predates roster reconciliation, so these figures use today's directory
            rather than the directory as it stood when the import ran.
          </p>
        )}
      </section>

      <section className="exec-section">
        <h2>
          Patient impact today
          <GuideLink section="dashboard-impact" spaced />
        </h2>
        <p className="exec-section-note">
          {number(liveImpact)} providers (P1 + P2) are affected in a way a patient would notice right now.
        </p>
        <div className="exec-impact">
          <button
            type="button"
            className="exec-card exec-card-critical"
            onClick={() => openAudit('P1')}
          >
            <span className="exec-card-head">
              <span className="exec-card-value">{number(impact.invisibleToPatients)}</span>
              <span className="priority-pill priority-p1 exec-card-tier">P1</span>
            </span>
            <span className="exec-card-label">Invisible to patients</span>
            <span className="exec-card-caption">
              Active and published, but nothing at all is bookable. A patient searching that location
              finds nothing.
            </span>
          </button>

          <button
            type="button"
            className="exec-card exec-card-warning"
            onClick={() => openAudit('P2')}
          >
            <span className="exec-card-head">
              <span className="exec-card-value">{number(impact.partiallyVisible)}</span>
              <span className="priority-pill priority-p2 exec-card-tier">P2</span>
            </span>
            <span className="exec-card-label">Partially visible</span>
            <span className="exec-card-caption">
              Bookable somewhere, but at least one published location shows no availability.
            </span>
          </button>

          <button
            type="button"
            className="exec-card exec-card-muted"
            onClick={() => openAudit('P3')}
          >
            <span className="exec-card-head">
              <span className="exec-card-value">{number(impact.notYetPatientFacing)}</span>
              <span className="priority-pill priority-p3 exec-card-tier">P3</span>
            </span>
            <span className="exec-card-label">Not yet patient-facing</span>
            <span className="exec-card-caption">
              The Epic fix is still needed, but none of the affected locations are published yet, so
              no patient is affected today.
            </span>
          </button>
        </div>
        <p className="exec-footnote">
          {number(impact.maintenanceOnly)} roster providers have no active department or only
          inactive departments. They are retained for maintenance and onboarding, but excluded from
          the opportunity and unresolved counts until they become eligible.{' '}
          <button
            type="button"
            className="exec-link"
            onClick={() => navigate('/provider-queue?maintenanceOnly=true')}
          >
            Review maintenance records
          </button>
          .
        </p>
        <p className="exec-footnote">
          A further {number(impact.inactiveExcluded)} providers appear in the Epic data but are not on
          the Open Scheduling roster — inactive in the directory, or not flagged for online
          scheduling. They are excluded from every figure above because fixing Epic would not change
          anything a patient sees.
        </p>
      </section>

      <section className="exec-section">
        <h2>Trends</h2>
        <p className="exec-section-note">
          Bookable providers and the P1, P2 and P3 counts across recent imports.
        </p>
        <MetricTrendChart weekly={metricTrend.weekly} nightly={metricTrend.nightly} />
      </section>

      <section className="exec-section">
        <h2>
          Progress toward resolution
          <GuideLink section="dashboard-resolution" spaced />
        </h2>
        <p className="exec-section-note">
          The coverage gap: {number(resolution.notVisible)} of the {number(coverage.total)} roster
          providers are not visible to patients ({number(coverage.total)} − {number(coverage.visible)}).
          P2 providers are not in this panel — they have slots somewhere, so they already count as
          visible.
        </p>
        <div className="exec-resolution-bridge">
          <span>
            <span className="priority-pill priority-p1">P1</span> {number(resolution.breakdown.p1)}{' '}
            invisible to patients
          </span>
          <span>
            <span className="priority-pill priority-p3">P3</span> {number(resolution.breakdown.p3)}{' '}
            not yet patient-facing
          </span>
          {resolution.breakdown.capacityOnly > 0 && (
            <span>{number(resolution.breakdown.capacityOnly)} fully booked, nothing blocked</span>
          )}
          {resolution.breakdown.maintenance > 0 && (
            <span>{number(resolution.breakdown.maintenance)} maintenance only</span>
          )}
          {resolution.excludedByWorkflow > 0 && (
            <span>− {number(resolution.excludedByWorkflow)} excluded by a note</span>
          )}
          {resolution.coveredByDepartmentItem > 0 && (
            <span>− {number(resolution.coveredByDepartmentItem)} covered by a department item</span>
          )}
          <strong>= {number(resolution.totalOpen)} provider work items</strong>
        </div>
        <p className="exec-section-note">
          Where those {number(resolution.totalOpen)} stand in the work queue. Every item starts as
          untriaged, so this reflects work actually picked up rather than work assumed to be in
          flight.
        </p>
        <div className="exec-funnel">
          {resolution.stages.map((stage) => (
            <div key={stage.label} className="exec-funnel-stage">
              <div className="exec-funnel-value">{number(stage.count)}</div>
              <span className="exec-funnel-label">{stage.label}</span>
              <div
                className="exec-funnel-bar"
                style={{ width: `${Math.round((stage.count / funnelPeak) * 100)}%` }}
              />
            </div>
          ))}
        </div>
        {resolution.noNoteYet > 0 && (
          <div className="exec-callout exec-callout-warning">
            <strong>{number(resolution.noNoteYet)}</strong> open items have no note against them at
            all, so nobody has yet looked at them.
          </div>
        )}
        {resolution.coveredByDepartmentItem > 0 && (
          <div className="exec-callout">
            <strong>{number(resolution.coveredByDepartmentItem)}</strong> providers are not counted
            above because an open department item already covers them — one Epic change resolves
            them together, and it is tracked once in the department queue.{' '}
            <button type="button" className="exec-link" onClick={() => navigate('/departments')}>
              See those department items
            </button>
            . They still appear under patient impact, which measures what a patient sees rather than
            how the work is tracked.
          </div>
        )}
        {resolution.excludedByWorkflow > 0 && (
          <div className="exec-callout">
            <strong>{number(resolution.excludedByWorkflow)}</strong> providers are not counted above
            because their latest note marks an intentional configuration or a temporary exclusion
            whose review date has not arrived. They remain visible in the work queue audit history.
          </div>
        )}
      </section>

      <section className="exec-section">
        <h2>
          Momentum
          <GuideLink section="dashboard-momentum" spaced />
        </h2>
        <p className="exec-section-note">
          {momentum.comparedWith
            ? `Movement since the ${shortDate(momentum.comparedWith)} import.`
            : 'No earlier import is available to compare against yet.'}
        </p>
        <div className="exec-momentum">
          <div>
            <div className={`exec-net exec-net-${netDirection}`}>
              {momentum.net > 0 ? '+' : ''}
              {number(momentum.net)}
            </div>
            <div className="exec-big-caption">Net providers made bookable</div>
          </div>
          <div>
            <div className="exec-card-value">{number(momentum.gained)}</div>
            <div className="exec-big-caption">Became bookable</div>
          </div>
          <div>
            <div className="exec-card-value">{number(momentum.lost)}</div>
            <div className="exec-big-caption">Stopped being bookable</div>
          </div>
          <div>
            <div className="exec-card-value">
              {momentum.agedOverThirtyDays === null ? '–' : number(momentum.agedOverThirtyDays)}
            </div>
            <div className="exec-big-caption">
              {momentum.agedOverThirtyDays === null
                ? 'Patient-impacting for 30+ days (not enough history yet)'
                : 'Patient-impacting for 30+ days'}
            </div>
          </div>
        </div>
        {momentum.coverageWarning && (
          <div className="exec-callout exec-callout-warning">{momentum.coverageWarning}</div>
        )}
      </section>

      {deepFindings && (
        <section className="exec-section">
          <h2>
            Beyond configuration: what the deep probe found
            <GuideLink section="levels" label="Level 1 vs Level 2" spaced />
          </h2>
          <p className="exec-section-note">
            Of the providers patients cannot book, {number(deepFindings.probed)} passed every
            configuration rule on at least one pairing and still returned no appointments. A second,
            deeper search over a wider window ({shortDate(deepFindings.snapshotDate)} import) separates
            the ones that are genuinely booked up from the ones Epic is hiding.
          </p>
          <div className="exec-funnel">
            <div className="exec-funnel-stage">
              <div className="exec-funnel-value exec-net-negative">{number(deepFindings.epicBuildProblem)}</div>
              <span className="exec-funnel-label">Epic build problem, not capacity</span>
              <span className="exec-card-caption">
                A fix exists in Epic; without it these providers stay unbookable.
              </span>
            </div>
            <div className="exec-funnel-stage">
              <div className="exec-funnel-value">{number(deepFindings.capacity)}</div>
              <span className="exec-funnel-label">Genuinely booked up</span>
              <span className="exec-card-caption">
                Full inside the 6-month window, or only bookable past it. No Epic change would help.
              </span>
            </div>
          </div>
          <table className="exec-causes">
            <thead>
              <tr>
                <th>Finding</th>
                <th>Providers</th>
                <th>Owner</th>
                <th>What it means</th>
              </tr>
            </thead>
            <tbody>
              {deepFindings.categories.map((category) => (
                <tr key={category.code}>
                  <td>{category.label}</td>
                  <td className="exec-cause-count">{number(category.providers)}</td>
                  <td>{category.owner || 'Unassigned'}</td>
                  <td className="exec-cause-remediation">{category.description}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="exec-footnote">
            A further {number(deepFindings.bookableElsewhere)} providers carry a deep finding on some
            pairing but are already bookable elsewhere. That is build-team detail rather than lost
            access; their visit-type fixes appear per department below.
          </p>
        </section>
      )}

      {departmentLeverage && (
        <section className="exec-section">
          <h2>
            Fix once, unblock many
            <GuideLink section="dashboard-leverage" spaced />
          </h2>
          <p className="exec-section-note">
            Departments where a single Epic change clears several providers at once —{' '}
            {number(departmentLeverage.actionableDepartments)} of them, covering{' '}
            {number(departmentLeverage.deptFlagFixes)} department-flag and{' '}
            {number(departmentLeverage.deepBuildFixes)} visit-type build fixes. Counted as
            provider-department fixes, not distinct providers: someone blocked in two clinics
            appears in both.
          </p>
          <table className="exec-causes">
            <thead>
              <tr>
                <th>Department</th>
                <th>One fix unblocks</th>
                <th>Fix</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {departmentLeverage.top.map((department) => (
                <tr key={department.departmentId}>
                  <td>{department.departmentName || department.departmentId}</td>
                  <td className="exec-cause-count">{number(department.providersBehindFix)}</td>
                  <td className="exec-cause-remediation">
                    {department.deptFlagProviderCount > 0
                      ? 'Turn on the department Open Scheduling flag.'
                      : `Visit-type build fix${department.topVisitType ? `, starting with ${department.topVisitType}.` : '.'}`}
                  </td>
                  <td>{department.noteStatus || 'Not yet triaged'}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="exec-footnote">
            Ranked only among departments published in Symplr, so a fix here is visible to patients.{' '}
            <button type="button" className="exec-link" onClick={() => navigate('/departments')}>
              Open the department work queue
            </button>
            .
          </p>
        </section>
      )}
    </div>
  );
};

export default ExecutiveDashboard;
